import React, { useState } from "react";
import PropTypes from "prop-types";
import { get } from "lodash-es";

// Material
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";

// Config
import colors from "src/config/colors";
import useTranslate from "src/hooks/useTranslate";
import { useMediaReupload } from "src/components/MediaItemList/MediaReuploadProvider";

// Icons
import trashIcon from "src/assets/icons/trash.svg";

// Components
import ConfirmationDialog from "src/components/ConfirmationDialog";
import MediaItemContent from "src/components/MediaItem/MediaItemContent";
import Text from "src/components/Text";
import UserAddition from "src/components/UserAddition";

const useStyles = makeStyles(theme => ({
  subTitle: {
    marginBottom: 12
  },
  perpetratorItem: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  perpetratorContent: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    paddingBottom: 12,
    borderBottom: "1px solid rgba(69, 79, 99, 0.15)"
  },
  imageWrapper: {
    width: 254
  },
  userAddition: {
    marginBottom: 0
  },
  footer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    flex: "1 1",
    display: "flex",
    alignItems: "center"
  },
  filename: {
    color: colors.santasGray,
    marginRight: theme.spacing(1)
  },
  deleteIcon: {
    height: 15
  }
}));

const MediaPerpetratorsItem = ({
  item,
  index,
  onFetchImage,
  isDeleting,
  onClick,
  onDelete,
  showDelete
}) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { openReupload } = useMediaReupload();

  const classes = useStyles();
  const getText = useTranslate();

  const handleDelete = () => {
    setDeleteOpen(false);
    onDelete();
  };

  const toggleDelete = status => () => setDeleteOpen(status);
  const handleOpenReupload = () => openReupload(item.id);

  const numberFilename = `${item.exhibitNumber} ${item.filename}`;

  return (
    <>
      <div className={classes.perpetratorItem}>
        <div>
          <Text className={classes.subTitle} type="heading4">
            {`${getText("caseIncidentsPage.perpetratorLabel")} ${index + 1}`}
          </Text>
        </div>
        <div className={classes.perpetratorContent}>
          <div className={classes.imageWrapper}>
            <MediaItemContent
              creationDateTime={item.creationDateTime}
              filename={`${item.exhibitNumber} ${item.filename}`}
              getImage={onFetchImage}
              imgSrc={get(item, "downloadURLs.thumbnailURL", "")}
              isProcessing={item.isProcessing}
              onClick={onClick}
              onOpenReupload={handleOpenReupload}
              status={item.status}
              minWidth="254px"
            />
          </div>

          <div>
            <UserAddition
              author={item.creator}
              className={classes.userAddition}
              date={item.creationDateTime}
              noBorder
              text={item.description}
            />
            <div className={classes.footer}>
              <Text type="subtitle1Small" className={classes.filename}>
                {numberFilename}
              </Text>
              {showDelete && (
                <IconButton onClick={toggleDelete(true)}>
                  <img className={classes.deleteIcon} src={trashIcon} alt="" />
                </IconButton>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        onClose={toggleDelete(false)}
        onConfirm={handleDelete}
        open={deleteOpen}
        textKeys={[
          "medicalRecordsPage.deleteButtonDialog.title",
          "medicalRecordsPage.deleteButtonDialog.description"
        ]}
        type="delete"
        button={getText("common.prompts.delete").toUpperCase()}
        cancel={getText("common.prompts.cancel").toUpperCase()}
        isLoading={isDeleting === item.id}
      />
    </>
  );
};

MediaPerpetratorsItem.propTypes = {
  item: PropTypes.shape({
    creationDateTime: PropTypes.string,
    creator: PropTypes.shape({
      fullName: PropTypes.string,
      profilePictureURL: PropTypes.string
    }),
    description: PropTypes.string,
    downloadURLs: PropTypes.shape({
      thumbnailURL: PropTypes.string
    }),
    filename: PropTypes.string,
    exhibitNumber: PropTypes.string,
    id: PropTypes.string,
    isProcessing: PropTypes.bool,
    status: PropTypes.string
  }).isRequired,
  onFetchImage: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  showDelete: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired
};

MediaPerpetratorsItem.defaultProps = {
  isDeleting: null
};

export default MediaPerpetratorsItem;
